import React from 'react';
import { Box, Stack, Tooltip, Typography } from '@mui/material';
import type { PayRule, PayRuleKind, PayrollLineStatus } from '../../api/services/payroll.service';

// The words and small pieces every payroll screen shares, so a day
// reads the same in the table, the inspector and the history.

export const money = (n: number | null | undefined, opts: { cents?: boolean } = {}): string => {
  if (n === null || n === undefined || Number.isNaN(n)) return '—';
  const abs = Math.abs(n);
  const showCents = opts.cents ?? abs % 1 !== 0;
  const s = abs.toLocaleString('en-US', {
    minimumFractionDigits: showCents ? 2 : 0, maximumFractionDigits: showCents ? 2 : 0,
  });
  return `${n < 0 ? '−' : ''}$${s}`;
};

export const count = (n: number | null | undefined): string => (
  n === null || n === undefined ? '—' : Number(n).toLocaleString('en-US')
);

export const percent = (n: number | null | undefined): string => (
  n === null || n === undefined ? '—' : `${n.toFixed(2)}%`
);

const utcDate = (iso: string) => new Date(iso);

/** "Sep 26 to Oct 2, 2026" for a week, "Sep 26" for a day. */
export const weekLabel = (start: string, end: string): string => {
  const a = utcDate(start);
  const b = utcDate(end);
  const month = (d: Date) => d.toLocaleDateString('en-US', { month: 'short', timeZone: 'UTC' });
  const day = (d: Date) => d.getUTCDate();
  const sameMonth = a.getUTCMonth() === b.getUTCMonth();
  return `${month(a)} ${day(a)} to ${sameMonth ? '' : `${month(b)} `}${day(b)}, ${b.getUTCFullYear()}`;
};

export const shortDate = (iso: string): string => utcDate(iso)
  .toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' });

export const weekdayShort = (iso: string): string => utcDate(iso)
  .toLocaleDateString('en-US', { weekday: 'short', timeZone: 'UTC' });

/** "Sat 9/26" */
export const dayLabel = (iso: string): string => {
  const d = utcDate(iso);
  return `${weekdayShort(iso)} ${d.getUTCMonth() + 1}/${d.getUTCDate()}`;
};

export const RULE_LABEL: Record<PayRuleKind, string> = {
  day_rate: 'Day rate',
  per_stop: 'Per stop',
  over_threshold: 'Stops over a threshold',
  hourly: 'Hourly',
  weekly_salary: 'Weekly salary',
  training_day: 'Training day',
  vacation_day: 'Vacation day',
  bonus: 'Bonus',
  deduction: 'Recurring deduction',
};

export const RULE_HINT: Record<PayRuleKind, string> = {
  day_rate: 'A flat amount for every day worked',
  per_stop: 'An amount for every stop',
  over_threshold: 'Extra per stop above a daily line, a weekly number, or a daily line pooled over the week',
  hourly: 'Hours are typed in each week',
  weekly_salary: 'Fixed, even when not in the statement',
  training_day: 'A rate for training days you enter',
  vacation_day: 'A rate for vacation days you enter',
  bonus: 'When days, stops or packages reach a number',
  deduction: 'Van rent, an advance: taken every week until removed',
};

/** The order the "Add a rule" menu lists them, earning first. */
export const RULE_KINDS_EARN: PayRuleKind[] = [
  'day_rate', 'per_stop', 'over_threshold', 'hourly', 'weekly_salary', 'training_day', 'vacation_day',
];
export const RULE_KINDS_OTHER: PayRuleKind[] = ['bonus', 'deduction'];

export const STATUS_LABEL: Record<PayrollLineStatus, string> = {
  ok: 'Calculated',
  no_rules: 'No pay rules',
  absent: 'Not in statement',
  skipped: 'Skipped',
};

export const ADJUSTMENT_LABEL = {
  bonus: 'Bonus',
  deduction: 'Deduction',
  vacation: 'Vacation pay',
  correction: 'Correction',
} as const;

/** A fresh rule of a kind, with sensible blanks. */
export function newRule(kind: PayRuleKind): PayRule {
  const id = `${kind}-${Math.random().toString(36).slice(2, 8)}`;
  switch (kind) {
    case 'over_threshold': return { id, kind, amount: null, threshold: 160, period: 'day', enabled: true };
    case 'bonus': return { id, kind, amount: null, threshold: 6, metric: 'days', enabled: true };
    case 'deduction': return { id, kind, amount: null, label: '', enabled: true };
    default: return { id, kind, amount: null, enabled: true };
  }
}

/** Two letters for a person. */
export const initials = (name: string): string => {
  const parts = String(name || '').trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return '?';
  return (parts[0][0] + (parts.length > 1 ? parts[parts.length - 1][0] : '')).toUpperCase();
};

/**
 * A person's mark. One tint for everyone, on purpose: colour in this
 * product means a state, and a person is not a state. The initials carry
 * the identity; the tint only says "this is a person".
 */
export const Avatar: React.FC<{ name: string; size?: number; muted?: boolean }> = ({ name, size = 32, muted }) => (
  <Box
    aria-hidden="true"
    sx={{
      width: size, height: size, borderRadius: '50%', flexShrink: 0,
      display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
      bgcolor: muted ? 'grey.100' : 'primary.light',
      color: muted ? 'text.secondary' : 'primary.dark',
      fontWeight: 700, fontSize: Math.round(size * 0.38), letterSpacing: '0.02em',
    }}
  >
    {initials(name)}
  </Box>
);

const WEEKDAYS = ['Sat', 'Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri'];

/**
 * Seven dots, Saturday to Friday, one per weekday the person appears in
 * the statement. Reads faster than "6" and says WHICH six.
 */
export const DayDots: React.FC<{ weekStart: string; days: { date: string }[] }> = ({ weekStart, days }) => {
  const worked = new Set(days.map((d) => Math.round((utcDate(d.date).getTime() - utcDate(weekStart).getTime()) / 86400000)));
  const label = `${worked.size} of 7 days: ${Array.from(worked).sort().map((i) => WEEKDAYS[i]).join(', ') || 'none'}`;
  return (
    <Tooltip title={label} placement="top">
      <Stack direction="row" gap={0.4} aria-label={label} sx={{ display: 'inline-flex' }}>
        {WEEKDAYS.map((w, i) => (
          <Box
            key={w}
            sx={{
              width: 9, height: 9, borderRadius: '50%',
              bgcolor: worked.has(i) ? 'primary.main' : 'grey.200',
            }}
          />
        ))}
      </Stack>
    </Tooltip>
  );
};

/** A small uppercase label above a group, the dashboard's "k" style. */
export const Eyebrow: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Typography variant="overline" sx={{ color: 'text.secondary', display: 'block', lineHeight: 1.6 }}>
    {children}
  </Typography>
);

/** a ÷ b as a percent with two decimals, the way every ratio here is shown. */
export const ratioPct = (part: number, whole: number): number | null => (whole ? Math.round((part / whole) * 10000) / 100 : null);

/**
 * The payroll ratio's arithmetic, on hover: what was divided by what. The
 * tile's number is worked out from the same figures, so the two agree.
 */
export const RatioMath: React.FC<{
  rows: { label: string; amount: number; over: string; base: number }[];
  children: React.ReactElement;
}> = ({ rows, children }) => (
  <Tooltip
    placement="bottom"
    title={(
      <Stack gap={0.75} sx={{ py: 0.25, fontVariantNumeric: 'tabular-nums' }}>
        {rows.map((r) => (
          <Box key={r.label}>
            <Stack direction="row" justifyContent="space-between" gap={2}><span>{r.label}</span><span>{money(r.amount, { cents: true })}</span></Stack>
            <Stack direction="row" justifyContent="space-between" gap={2}><span>÷ {r.over}</span><span>{money(r.base, { cents: true })}</span></Stack>
            <Stack direction="row" justifyContent="space-between" gap={2} sx={{ fontWeight: 700, borderTop: '1px solid', borderColor: 'divider', mt: 0.25, pt: 0.25 }}>
              <span>=</span><span>{percent(ratioPct(r.amount, r.base))}</span>
            </Stack>
          </Box>
        ))}
      </Stack>
    )}
  >
    <Box sx={{ height: '100%' }}>{children}</Box>
  </Tooltip>
);

/**
 * A change against last week, coloured by whether it is good news: green
 * when it moved the way you want, red when it did not. `better` says which
 * way that is for this number (less payroll is better, more revenue is).
 */
export const Delta: React.FC<{
  diff: number | null; better: 'up' | 'down'; format: (n: number) => string;
  /** What the change is measured against. */
  against?: string;
}> = ({ diff, better, format, against = 'last week' }) => {
  if (diff === null) return null;
  const same = Math.abs(diff) < 0.005;
  const good = same ? null : (diff > 0) === (better === 'up');
  return (
    <Box data-tone={good === null ? 'same' : good ? 'good' : 'bad'} sx={{ lineHeight: 1.25 }}>
      <Typography component="div" variant="body2" sx={{ fontWeight: 700, fontVariantNumeric: 'tabular-nums', color: good === null ? 'text.secondary' : good ? 'success.dark' : 'error.dark' }}>
        {same ? 'Same' : `${diff > 0 ? '▴' : '▾'} ${format(Math.abs(diff))}`}
      </Typography>
      <Typography component="div" variant="caption" color="text.secondary">{same ? `as ${against}` : `vs ${against}`}</Typography>
    </Box>
  );
};
